import { useEffect, useState } from 'react';

import AdminConfirmDialog from './AdminConfirmDialog';

type AdminPasswordConfirmDialogProps = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isConfirming?: boolean;
  errorMessage?: string | null;
  confirmationPhrase?: string | null;
  confirmationPhraseLabel?: string;
  onConfirm: (password: string, confirmationPhrase?: string) => void;
  onCancel: () => void;
};

export default function AdminPasswordConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  isConfirming = false,
  errorMessage = null,
  confirmationPhrase = null,
  confirmationPhraseLabel,
  onConfirm,
  onCancel,
}: AdminPasswordConfirmDialogProps) {
  const [password, setPassword] = useState('');
  const [phrase, setPhrase] = useState('');

  useEffect(() => {
    if (!open) {
      setPassword('');
      setPhrase('');
    }
  }, [open]);

  const phraseRequired = Boolean(confirmationPhrase);
  const phraseOk = !phraseRequired || phrase.trim() === confirmationPhrase;
  const canConfirm = password.trim().length > 0 && phraseOk && !isConfirming;

  return (
    <AdminConfirmDialog
      open={open}
      title={title}
      description={
        <div className="space-y-3 text-sm text-slate-700">
          <p>{description}</p>
          <div className="space-y-2">
            <label htmlFor="admin-password-confirm" className="text-xs font-semibold text-slate-900">
              Votre mot de passe
            </label>
            <input
              id="admin-password-confirm"
              type="password"
              autoComplete="current-password"
              value={password}
              disabled={isConfirming}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm"
              placeholder="Mot de passe administrateur"
            />
          </div>
          {phraseRequired ? (
            <div className="space-y-2">
              <label htmlFor="admin-phrase-confirm" className="text-xs font-semibold text-slate-900">
                {confirmationPhraseLabel ?? `Saisissez « ${confirmationPhrase} » pour confirmer`}
              </label>
              <input
                id="admin-phrase-confirm"
                type="text"
                value={phrase}
                disabled={isConfirming}
                onChange={(event) => setPhrase(event.target.value)}
                className="w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm"
                placeholder={confirmationPhrase ?? ''}
              />
            </div>
          ) : null}
        </div>
      }
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      variant="danger"
      isConfirming={isConfirming}
      errorMessage={errorMessage}
      onCancel={onCancel}
      onConfirm={() => onConfirm(password, phraseRequired ? phrase : undefined)}
      confirmDisabled={!canConfirm}
    />
  );
}
